import { useCallback, useReducer } from 'react';

/*
 * The ResUI toast stack's state (PRD-1702, ResUI Design Decisions, Toast).
 * Newest on top, three at most: a fourth clears the oldest Neutral or Success
 * toast, and when all three stay until dismissed it waits its turn.
 */

// The type callers already pass to onShowNotification, as a toast's tone.
const TONE = { success: 'success', info: 'neutral', neutral: 'neutral', alert: 'warn', warn: 'warn', error: 'error' };
const MAX = 3;
// Neutral and Success restate what the screen shows, so they clear themselves.
export const clears = (toast) => toast.tone === 'success' || toast.tone === 'neutral';

function reduce(state, action) {
  if (action.type === 'show') {
    const { shown, waiting } = state;
    if (shown.length < MAX) return { shown: [action.toast, ...shown], waiting };
    let drop = -1;
    shown.forEach((t, i) => { if (clears(t)) drop = i; }); // the oldest that clears
    if (drop < 0) return { shown, waiting: [...waiting, action.toast] };
    return { shown: [action.toast, ...shown.filter((_, i) => i !== drop)], waiting };
  }
  if (action.type === 'dismiss') {
    const shown = state.shown.filter((t) => t.id !== action.id);
    if (shown.length === state.shown.length || !state.waiting.length) return { ...state, shown };
    const [next, ...waiting] = state.waiting;
    return { shown: [next, ...shown], waiting };
  }
  return state;
}

let nextId = 0;

/** `show(message, type)` takes the same arguments as onShowNotification. */
export function useToaster() {
  const [state, dispatch] = useReducer(reduce, { shown: [], waiting: [] });
  const show = useCallback((message, type = 'success') => {
    nextId += 1;
    dispatch({ type: 'show', toast: { id: nextId, message, tone: TONE[type] || 'neutral' } });
  }, []);
  const dismiss = useCallback((id) => dispatch({ type: 'dismiss', id }), []);
  return { toasts: state.shown, show, dismiss };
}
